import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Mail, 
  Send, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  AlertCircle,
  RefreshCw,
  Search,
  Filter,
  BarChart3,
  ShoppingCart,
  Trash2,
  Play,
  TrendingUp
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Email type categories for grouping
const EMAIL_CATEGORIES = {
  transactional: ['welcome', 'email_verification', 'password_reset', 'password_changed', 'order_confirmation', 'payment_receipt', 'invoice_email', 'payment_failed', 'refund_processed', 'registration_confirmed', 'profile_updated', 'account_deactivated'],
  webinar_lifecycle: ['webinar_reminder_7_days', 'webinar_reminder_24_hours', 'webinar_reminder_1_hour', 'webinar_starting_now', 'webinar_missed', 'post_webinar_thank_you', 'recording_available', 'certificate_ready', 'cpe_credits_issued', 'webinar_rescheduled', 'webinar_cancelled'],
  behavior_based: ['abandoned_checkout_1hr', 'abandoned_checkout_24hr', 'abandoned_checkout_72hr', 'browse_abandonment', 'wishlist_reminder', 'price_drop_alert', 'almost_sold_out', 'back_in_stock'],
  re_engagement: ['we_miss_you_7_days', 'we_miss_you_30_days', 'we_miss_you_60_days', 'we_miss_you_90_days', 'incomplete_profile', 'no_purchase_yet'],
  milestones: ['first_webinar_completed', 'milestone_5_webinars', 'milestone_10_webinars', 'cpe_goal_progress', 'annual_summary', 'birthday_email', 'anniversary_email'],
  marketing: ['new_webinar_announcement', 'speaker_spotlight', 'industry_news_digest', 'recommended_for_you', 'flash_sale', 'early_bird_access', 'bundle_offer', 'seasonal_campaign', 'referral_program'],
  lead_nurturing: ['lead_welcome', 'lead_nurture_day_3', 'lead_nurture_day_7', 'lead_nurture_day_14', 'lead_nurture_day_21', 'lead_status_changed', 'lead_assigned'],
  corporate: ['corporate_welcome', 'team_member_invited', 'team_usage_report', 'license_expiring', 'license_renewal', 'new_seats_added'],
  admin: ['admin_new_user', 'admin_new_order', 'admin_high_value_lead', 'admin_support_ticket', 'admin_webinar_full', 'admin_daily_summary', 'admin_weekly_report']
};

const getCategoryColor = (emailType) => {
  for (const [category, types] of Object.entries(EMAIL_CATEGORIES)) {
    if (types.includes(emailType)) {
      switch (category) {
        case 'transactional': return 'bg-blue-100 text-blue-800';
        case 'webinar_lifecycle': return 'bg-green-100 text-green-800';
        case 'behavior_based': return 'bg-yellow-100 text-yellow-800';
        case 're_engagement': return 'bg-purple-100 text-purple-800';
        case 'milestones': return 'bg-pink-100 text-pink-800';
        case 'marketing': return 'bg-orange-100 text-orange-800';
        case 'lead_nurturing': return 'bg-cyan-100 text-cyan-800';
        case 'corporate': return 'bg-indigo-100 text-indigo-800';
        case 'admin': return 'bg-slate-100 text-slate-800';
        default: return 'bg-gray-100 text-gray-800';
      }
    }
  }
  return 'bg-gray-100 text-gray-800';
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'sent':
    case 'delivered':
      return <CheckCircle2 className="w-4 h-4 text-green-600" />;
    case 'failed':
    case 'bounced':
      return <XCircle className="w-4 h-4 text-red-600" />;
    case 'pending':
      return <Clock className="w-4 h-4 text-yellow-600" />;
    default:
      return <AlertCircle className="w-4 h-4 text-gray-600" />;
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case 'sent':
    case 'delivered':
      return 'bg-green-100 text-green-800';
    case 'failed':
    case 'bounced':
      return 'bg-red-100 text-red-800';
    case 'pending':
      return 'bg-yellow-100 text-yellow-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

export default function AdminEmailsPage() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [emailStats, setEmailStats] = useState(null);
  const [emailLogs, setEmailLogs] = useState([]);
  const [scheduledEmails, setScheduledEmails] = useState([]);
  const [abandonedCarts, setAbandonedCarts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [testEmailType, setTestEmailType] = useState('welcome');
  const [testEmailTo, setTestEmailTo] = useState('');

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token, activeTab]);

  const fetchData = async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      // Fetch stats
      const statsRes = await axios.get(`${API_URL}/api/admin/emails/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailStats(statsRes.data);

      // Fetch based on active tab
      if (activeTab === 'logs') {
        const logsRes = await axios.get(`${API_URL}/api/admin/emails/logs`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { per_page: 50, status: filterStatus || undefined }
        });
        setEmailLogs(logsRes.data.logs);
      } else if (activeTab === 'scheduled') {
        const scheduledRes = await axios.get(`${API_URL}/api/admin/emails/scheduled`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { per_page: 50, status: 'pending' }
        });
        setScheduledEmails(scheduledRes.data.emails);
      } else if (activeTab === 'abandoned') {
        const abandonedRes = await axios.get(`${API_URL}/api/admin/emails/abandoned-carts`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { per_page: 50 }
        });
        setAbandonedCarts(abandonedRes.data.carts);
      }
    } catch (error) {
      console.error('Failed to fetch email data:', error);
      toast.error('Failed to load email data');
    }
    setLoading(false);
  };

  const handleProcessScheduled = async () => {
    try {
      await axios.post(`${API_URL}/api/admin/emails/process-scheduled`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Scheduled emails processed');
      fetchData();
    } catch (error) {
      toast.error('Failed to process scheduled emails');
    }
  };

  const handleCancelScheduled = async (emailId) => {
    try {
      await axios.delete(`${API_URL}/api/admin/emails/scheduled/${emailId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Scheduled email cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel scheduled email');
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmailTo) {
      toast.error('Please enter an email address');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/admin/emails/send-test`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: { email_type: testEmailType, to_email: testEmailTo }
      });
      toast.success(`Test email sent to ${testEmailTo}`);
      setTestEmailTo('');
    } catch (error) {
      toast.error('Failed to send test email');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  const filteredLogs = emailLogs.filter(log => {
    if (searchTerm) {
      return log.to_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
             log.email_type?.toLowerCase().includes(searchTerm.toLowerCase());
    }
    return true;
  });

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'logs', label: 'Email Logs', icon: Mail },
    { id: 'scheduled', label: 'Scheduled', icon: Clock },
    { id: 'abandoned', label: 'Abandoned Carts', icon: ShoppingCart },
  ];

  return (
    <div data-testid="admin-emails-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-navy-600">Email Campaigns</h1>
        <Button onClick={fetchData} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 bg-slate-100 rounded-lg p-1">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-white text-navy-600 shadow-sm'
                : 'text-slate-600 hover:text-navy-600'
            }`}
          >
            <tab.icon className="w-4 h-4 mr-2" />
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy-600"></div>
        </div>
      ) : (
        <>
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Total Sent</p>
                        <p className="text-3xl font-bold text-navy-600">{emailStats?.total_sent || 0}</p>
                      </div>
                      <div className="p-3 bg-green-100 rounded-lg">
                        <Send className="w-6 h-6 text-green-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Failed</p>
                        <p className="text-3xl font-bold text-red-600">{emailStats?.total_failed || 0}</p>
                      </div>
                      <div className="p-3 bg-red-100 rounded-lg">
                        <XCircle className="w-6 h-6 text-red-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Pending Scheduled</p>
                        <p className="text-3xl font-bold text-yellow-600">{emailStats?.total_pending_scheduled || 0}</p>
                      </div>
                      <div className="p-3 bg-yellow-100 rounded-lg">
                        <Clock className="w-6 h-6 text-yellow-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Last 7 Days</p>
                        <p className="text-3xl font-bold text-blue-600">{emailStats?.recent_sent_7_days || 0}</p>
                      </div>
                      <div className="p-3 bg-blue-100 rounded-lg">
                        <TrendingUp className="w-6 h-6 text-blue-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Email Types Distribution */}
              {emailStats?.by_type?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Top Email Types</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {emailStats.by_type.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getCategoryColor(item.email_type)}`}>
                            {item.email_type.replace(/_/g, ' ')}
                          </span>
                          <span className="font-semibold text-slate-700">{item.count}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Send Test Email */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Send Test Email</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-4">
                    <select
                      value={testEmailType}
                      onChange={(e) => setTestEmailType(e.target.value)}
                      className="px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy-500"
                    >
                      {Object.entries(EMAIL_CATEGORIES).map(([category, types]) => (
                        <optgroup key={category} label={category.replace(/_/g, ' ').toUpperCase()}>
                          {types.map(type => (
                            <option key={type} value={type}>{type.replace(/_/g, ' ')}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    <Input
                      type="email"
                      placeholder="Recipient email"
                      value={testEmailTo}
                      onChange={(e) => setTestEmailTo(e.target.value)}
                      className="w-64"
                    />
                    <Button onClick={handleSendTestEmail}>
                      <Send className="w-4 h-4 mr-2" />
                      Send Test
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Email Categories */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Email Campaign Categories (72 Templates)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {Object.entries(EMAIL_CATEGORIES).map(([category, types]) => (
                      <div key={category} className="p-4 bg-slate-50 rounded-lg">
                        <h4 className="font-semibold text-navy-600 capitalize mb-2">
                          {category.replace(/_/g, ' ')} ({types.length})
                        </h4>
                        <div className="flex flex-wrap gap-1">
                          {types.slice(0, 5).map(type => (
                            <span key={type} className={`px-2 py-0.5 rounded text-xs ${getCategoryColor(type)}`}>
                              {type.replace(/_/g, ' ')}
                            </span>
                          ))}
                          {types.length > 5 && (
                            <span className="px-2 py-0.5 rounded text-xs bg-slate-200 text-slate-600">
                              +{types.length - 5} more
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Email Logs Tab */}
          {activeTab === 'logs' && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Email Logs</CardTitle>
                  <div className="flex gap-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Search emails..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 w-64"
                      />
                    </div>
                    <select
                      value={filterStatus}
                      onChange={(e) => {
                        setFilterStatus(e.target.value);
                        fetchData();
                      }}
                      className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    >
                      <option value="">All Status</option>
                      <option value="sent">Sent</option>
                      <option value="failed">Failed</option>
                      <option value="pending">Pending</option>
                    </select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {filteredLogs.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Mail className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>No email logs found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Type</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Recipient</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Subject</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Status</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Sent At</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredLogs.map((log) => (
                          <tr key={log.id} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-3 px-4">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${getCategoryColor(log.email_type)}`}>
                                {log.email_type?.replace(/_/g, ' ')}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-sm">{log.to_email}</td>
                            <td className="py-3 px-4 text-sm max-w-xs truncate">{log.subject}</td>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                {getStatusIcon(log.status)}
                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(log.status)}`}>
                                  {log.status}
                                </span>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-sm text-slate-500">{formatDate(log.sent_at || log.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Scheduled Emails Tab */}
          {activeTab === 'scheduled' && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Scheduled Emails</CardTitle>
                  <Button onClick={handleProcessScheduled} size="sm">
                    <Play className="w-4 h-4 mr-2" />
                    Process Now
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {scheduledEmails.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Clock className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>No scheduled emails</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Type</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Recipient</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Scheduled For</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Status</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {scheduledEmails.map((email) => (
                          <tr key={email.id} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-3 px-4">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${getCategoryColor(email.email_type)}`}>
                                {email.email_type?.replace(/_/g, ' ')}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-sm">{email.to_email}</td>
                            <td className="py-3 px-4 text-sm">{formatDate(email.scheduled_for)}</td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(email.status)}`}>
                                {email.status}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              {email.status === 'pending' && (
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => handleCancelScheduled(email.id)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Abandoned Carts Tab */}
          {activeTab === 'abandoned' && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Abandoned Carts</CardTitle>
              </CardHeader>
              <CardContent>
                {abandonedCarts.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <ShoppingCart className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>No abandoned carts tracked yet</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Email</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Webinar</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Amount</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Emails Sent</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Status</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {abandonedCarts.map((cart) => (
                          <tr key={cart.id} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-3 px-4 text-sm">{cart.email}</td>
                            <td className="py-3 px-4 text-sm max-w-xs truncate">{cart.webinar_title}</td>
                            <td className="py-3 px-4 text-sm font-semibold">${cart.amount?.toFixed(2)}</td>
                            <td className="py-3 px-4">
                              <div className="flex gap-1">
                                <span className={`px-2 py-0.5 rounded text-xs ${cart.email_1hr_sent ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-600'}`}>
                                  1hr
                                </span>
                                <span className={`px-2 py-0.5 rounded text-xs ${cart.email_24hr_sent ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-600'}`}>
                                  24hr
                                </span>
                                <span className={`px-2 py-0.5 rounded text-xs ${cart.email_72hr_sent ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-600'}`}>
                                  72hr
                                </span>
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                cart.recovered ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {cart.recovered ? 'Recovered' : 'Abandoned'}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-sm text-slate-500">{formatDate(cart.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
